import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Divider } from '@mui/material'
import { CommonTextField, CommonButton, CommonPhone } from '@/components/common'
import { ROUTES, STORAGE_KEYS, loginWithoutPasswordSchema } from '@/constants'
import { useLoginWithMagicLink } from '@/hooks'
import { localStorageService } from '@/services'
import { routeToRedirectUrl } from '@/utils'

export const LoginWithoutPasswordPage = () => {
  const navigate = useNavigate()
  const { loadingLoginWithMagicLink, loginWithMagicLink } =
    useLoginWithMagicLink()

  const [step, setStep] = useState(1)
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [errors, setErrors] = useState({})

  const redirectUrl = localStorageService.getItem(STORAGE_KEYS.REDIRECT_URL)

  const handleLogin = async e => {
    if (e) e.preventDefault()
    setErrors({})

    try {
      let isEmailLogin = false
      if (email) isEmailLogin = true

      const validData = isEmailLogin ? { email } : { phoneNumber }

      await loginWithoutPasswordSchema.validate(validData, {
        abortEarly: false,
      })

      const response = await loginWithMagicLink({ ...validData, redirectUrl })
      if (response) setStep(2)
      setEmail('')
      setPhoneNumber('')
    } catch (err) {
      setStep(3)
      console.log('err', err)
    }
  }

  const handleContinueAsGuest = () => {
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST, 'true')
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST_AT, Date.now().toString())

    if (redirectUrl) routeToRedirectUrl(redirectUrl)
    else navigate(ROUTES.HOME)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component='form'
        onSubmit={handleLogin}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 700,
          gap: 3,
        }}
      >
        <Typography
          variant='h4'
          fontWeight={500}
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          {step === 1 && (
            <>
              Enter your contact information, and we'll send a link to login
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Search by either email or phone number
              </Typography>
            </>
          )}
          {step === 2 && (
            <>
              Magic Link is on the way!
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Your magic link has been sent to your contact information
              </Typography>
            </>
          )}

          {step === 3 && (
            <>
              We did not find a donor record with that contact information
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Enter another email or phone to try again
              </Typography>
            </>
          )}
        </Typography>

        <CommonTextField
          id='email'
          label='Email'
          value={email}
          setValue={setEmail}
          type='email'
          error={errors.email}
          disabled={!!phoneNumber}
          placeholder='Enter your email'
          inputProps={{
            name: 'email',
            autoComplete: 'username email',
          }}
        />

        <Divider sx={{ width: '100%', color: 'text.secondary' }}>OR</Divider>

        <CommonPhone
          id='phone-number'
          label='Phone Number'
          value={phoneNumber}
          setValue={setPhoneNumber}
          mask='(999) 999-9999'
          error={errors.phoneNumber}
          disabled={!!email}
          placeholder='(###)-###-####'
          inputProps={{
            name: 'phoneNumber',
          }}
        />

        <CommonButton
          id='login-button'
          label='Send Link'
          type='submit'
          onClick={handleLogin}
          loading={loadingLoginWithMagicLink}
          disabled={!email && !phoneNumber}
          size='large'
          style={{ width: '100%' }}
        />

        {step !== 1 && (
          <CommonButton
            id='continue-as-guest-button'
            label='Continue as Guest'
            link
            variant='text'
            onClick={handleContinueAsGuest}
            size='small'
            sx={{ fontWeight: 400, mt: 2 }}
          />
        )}
      </Box>
    </Box>
  )
}
